import React, { useEffect, useState } from 'react';
import { Row, Col, Container } from 'react-bootstrap';
import {
  listMaskingJobs,
  listEncryptJobs,
  listDecryptJobs,
  getMaskingJob,
  transformMaskingJobsToReportRows,
  transformEncryptionJobsToReportRows,
} from 'Secure/api';
import MaskingReportsTable from './MaskingReportsTable';

// Drain all pages of a paged job list (listFn returns a Spring Page: { content, totalPages }).
async function fetchAllJobs(listFn, fetchSize) {
  let page = 0;
  let totalPages = 1;
  const all = [];
  while (page < totalPages) {
    const res = await listFn({ page, size: fetchSize, sort: ['id,desc'] });
    const content = res && Array.isArray(res.content) ? res.content : [];
    all.push(...content);
    totalPages = res && res.totalPages != null ? Number(res.totalPages) : 1;
    page += 1;
  }
  return all;
}

const MaskingReports = () => {
  const [fetchingData, setFetchingData] = useState(false);
  const [resultSets, setResultSets] = useState([]);
  const [error, setError] = useState(null);

  const PAGE_SIZE = 10;
  const FETCH_SIZE = 200;

  const loadJobsHandler = async () => {
    setFetchingData(true);
    setError(null);
    try {
      // Masking, encrypt, and decrypt jobs all show in one Job Results table. Fetch in parallel;
      // each transform groups its own job family into parent/child rows, then we concatenate.
      const [maskJobs, encryptJobs, decryptJobs] = await Promise.all([
        fetchAllJobs(listMaskingJobs, FETCH_SIZE),
        fetchAllJobs(listEncryptJobs, FETCH_SIZE),
        fetchAllJobs(listDecryptJobs, FETCH_SIZE),
      ]);
      // The list's progress counters are only as fresh as the platform's own worker poll
      // (30s). The single-job endpoint LIVE-SYNCS an active job with the worker, so swap
      // in its DTO for running jobs — that feeds the running row's progress bar.
      await Promise.all(
        maskJobs.map(async (job, i) => {
          if (!job || (job.status !== 'RUNNING' && job.status !== 'PENDING')) return;
          try {
            const live = await getMaskingJob(job.id);
            if (live && live.id === job.id) maskJobs[i] = live;
          } catch (e) {
            // Stale row still renders; next poll retries.
          }
        })
      );
      const rows = [
        ...transformMaskingJobsToReportRows(maskJobs, 0),
        ...transformEncryptionJobsToReportRows(encryptJobs, decryptJobs, 0),
      ];
      setResultSets(rows);
    } catch (e) {
      console.log(e);
      setError(e.message);
      setResultSets([]);
    }
    setFetchingData(false);
  };

  useEffect(() => {
    loadJobsHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll so a running job clears (and a cancel/re-run reflects) without a manual reload.
  // 5s while a job is running — the running row's progress bar feeds off these refreshes —
  // else 30s.
  const anyRunning = (resultSets || []).some((r) => r && r.status === 'RUNNING');
  useEffect(() => {
    const timer = setInterval(loadJobsHandler, anyRunning ? 5000 : 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyRunning]);

  return (
    <div>
      <Container fluid className='p-0'>
        <h5 className='text-left mb-3 h-secondary'>JOB RESULTS</h5>
        {error && (
          <Row className='widget-row'>
            <Col>
              <div className='text-danger p-2' data-testid='masking-reports-error'>{error}</div>
            </Col>
          </Row>
        )}
        <Row className='widget-row'>
          <Col>
            <MaskingReportsTable
              resultSets={resultSets}
              pageSize={PAGE_SIZE}
              fetchingData={fetchingData}
              onReload={loadJobsHandler}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default MaskingReports;
